import Vue from 'vue'
import 'es6-promise/auto' // Vuex requires Promise; some browsers do not implement Promise (e.g. IE)
import Vuex from 'vuex'
import _ from 'lodash'
import i18n from '@/i18n'
import Pako from 'pako'

import api from '@/services/api'
import { API_CONFIG } from '@/config'

Vue.use(Vuex)

const error = (err, reject) => {
  let message, status
  const authErrors = [ 401, 403 ]
  const { response } = err
  if (response) {
    const { data } = response
    status = response.status
    switch (typeof data) {
      case 'string':
        message = i18n.t(data)
        break
      case 'object':
        if (data.message && data.args) message = i18n.t(data.message, data.args)
        break
    }
  }
  store.dispatch('setNotification', { message, isAuthError: authErrors.includes(status) })
  reject(err)
}

const getEmptyFilterObject = (filter, unset = false) => {
  const { collection, type, options, defaults, multiple } = filter
  const filterObj = {
    collection,
    type,
    values: (multiple) ? [] : ''
  }
  if (type === 'hierarchical') {
    let values = {}
    _.each(options, option => {
      values[option.name] = []
    })
    filterObj.values = values
  } else {
    if (defaults && defaults.length !== 0 && !unset) {
      if (multiple) {
        _.each(defaults, value => {
          filterObj.values.push(value)
        })
      } else {
        filterObj.values = defaults[0]
      }
    }
  }
  return filterObj
}

const getEmptyFilters = filters => {
  let emptyFilters = {}
  _.each(filters, filter => {
    emptyFilters[filter.name] = getEmptyFilterObject(filter)
  })
  return emptyFilters
}

const store = new Vuex.Store({
  state: {
    loading: false,
    notification: { show: false },
    sidebarType: 'clipped',
    sidebarActive: false,
    screenSize: '',
    user: {},
    filters: [],
    filterValues: { filters: {}, keywords: '', sort: '' }
  },
  mutations: {
    setLoading (state, bool) {
      state.loading = bool
    },
    setNotification (state, data) {
      state.loading = false
      state.notification = {
        show: data.show !== false,
        message: data.message || i18n.t('error.loading'),
        isAuthError: data.isAuthError || false
      }
    },
    setClient (state, client) {
      state.user.client = client
      localStorage.setItem('user', JSON.stringify(state.user))
    },
    setUser (state, user) {
      localStorage.setItem('user', JSON.stringify(user))
      state.user = user
    },
    refreshUser (state) {
      if (localStorage.getItem('user')) {
        state.user = JSON.parse(localStorage.getItem('user'))
      }
    },
    setFilters (state, filters) {
      state.filters = filters
    },
    setSidebarType (state, type) {
      state.sidebarType = type
    },
    setSidebarActive (state, bool) {
      state.sidebarActive = bool
    },
    setScreenSize (state, size) {
      state.screenSize = size
    },
    setEmptyFilterValues (state) {
      state.filterValues.filters = getEmptyFilters(state.filters)
    },
    unsetFilterVariable (state, filter) {
      state.filterValues.filters[filter.name] = getEmptyFilterObject(filter, true)
    }
  },
  actions: {
    setLoading ({commit}, bool) {
      commit('setLoading', bool)
    },
    setSidebarType ({commit}, type) {
      commit('setSidebarType', type)
    },
    setSidebarActive ({commit}, bool) {
      commit('setSidebarActive', bool)
    },
    setScreenSize ({commit}, size) {
      commit('setScreenSize', size)
    },
    setEmptyFilterValues ({commit}) {
      commit('setEmptyFilterValues')
    },
    unsetFilterVariable ({commit}, filter) {
      commit('unsetFilterVariable', filter)
    },
    setNotification ({commit}, data) {
      commit('setNotification', data)
    },
    setClient ({commit}, client) {
      commit('setClient', client)
    },
    setUser ({commit}, user) {
      commit('setUser', user)
    },
    refreshUser ({commit}) {
      commit('refreshUser')
    },
    resetUser ({commit}) {
      commit('setUser', {})
    },
    getTranslations ({ state }) {
      return new Promise((resolve, reject) => {
        api.call('getTranslations', {
          clientId: state.user.client._id
        }, translations => {
          _.each(Object.keys(translations), locale => {
            i18n.mergeLocaleMessage(locale, { filters: translations[locale] })
          })
          resolve()
        }, err => error(err, reject))
      })
    },
    getUser ({ commit }) {
      return new Promise((resolve, reject) => {
        api.call('getUser', null, user => {
          commit('setUser', user)
          resolve()
        }, err => error(err, reject))
      })
    },
    getFilters ({ commit, state }) {
      return new Promise((resolve, reject) => {
        api.call('getFilters', {
          clientId: state.user.client._id
        }, filters => {
          commit('setFilters', filters)
          commit('setEmptyFilterValues')
          resolve()
        }, err => error(err, reject))
      })
    },
    getResults ({ commit, state }, config) {
      const { page, urlFilterValues } = config
      const filterValues = { filters: {}, keywords: '', sort: '' }

      if (urlFilterValues) {
        // Function to deep merge url filter values and default filter values
        const isObject = item => item && typeof item === 'object' && !Array.isArray(item)
        const deepMerge = (target, ...sources) => {
          if (!sources.length) return target
          const source = sources.shift()
          if (isObject(target) && isObject(source)) {
            for (const key in source) {
              if (isObject(source[key])) {
                if (!target.hasOwnProperty(key)) throw new Error('Invalid filter')
                if (!target[key]) target[key] = {}
                deepMerge(target[key], source[key])
              } else {
                if (!target.hasOwnProperty(key)) throw new Error('Invalid filter')
                target[key] = source[key]
              }
            }
          }
          return deepMerge(target, ...sources)
        }
        try {
          // Decode url filter values
          // const decodedUrlFilterValues = JSON.parse(Pako.ungzip(Pako.inflateRaw(atob(decodeURIComponent(urlFilterValues)), { to: 'string' }), {raw: true, to: 'string'}))
          // New decoding technique
          const decodedUrlFilterValues = JSON.parse(Pako.inflate(Uint8Array.from(atob(decodeURIComponent(urlFilterValues)), c => c.charCodeAt(0)), { to: 'string' }))

          // Merge default filter values and url filter values
          const emptyFilterValues = filterValues
          emptyFilterValues.filters = getEmptyFilters(state.filters)
          // Set filter values
          state.filterValues = deepMerge(emptyFilterValues, decodedUrlFilterValues)
        } catch (err) {
          return new Promise((resolve, reject) => {
            const e = typeof err !== 'string' ? err : {}
            e.response = { data: 'error.url_filter_is_invalid' }
            error(e, reject)
          })
        }
      }

      // Filter encoding example
      // const d = { filters: { series: { collection: 'slideshows', type: 'categorical', values: [ 'X166' ] }, view: { collection: 'slides', type: 'categorical', values: 'Detailcharts' }, coding: { collection: 'slides', type: 'hierarchical', values: { Assistenzsysteme: [ 'Distronic', 'Parktronic' ] } } }, keywords: 'mercedes benz', sort: 'newest' }
      // console.log('localhost:8080?filter=' + encodeURIComponent(btoa(Pako.deflateRaw(Pako.gzip(JSON.stringify(d), { raw: true, to: 'string' }), { to: 'string' }))))
      // New encoding technique
      // console.log('localhost:8080?filter=' + encodeURIComponent(Buffer.from(Pako.deflate(JSON.stringify(d))).toString('base64')))

      const { filters, keywords, sort } = state.filterValues

      if (!state.loading) commit('setLoading', true)

      const prepareFilterObj = (v, k) => {
        if (!_.has(filterValues.filters, v.collection)) filterValues.filters[v.collection] = {}
        if (!_.has(filterValues.filters[v.collection], k)) filterValues.filters[v.collection][k] = {}
        filterValues.filters[v.collection][k].type = v.type
      }

      _.each(filters, (v, k) => {
        if (v.type === 'categorical') {
          // Check if filter values have been added
          if (_.isArray(v.values) && v.values.length !== 0) {
            prepareFilterObj(v, k)
            // Add values to filter object
            filterValues.filters[v.collection][k].values = v.values
          } else if (_.isString(v.values) && v.values !== '') {
            prepareFilterObj(v, k)
            // Add values to filter object
            filterValues.filters[v.collection][k].values = [v.values]
          }
        } else if (v.type === 'hierarchical') {
          _.each(v.values, (val, key) => {
            // Check if filter values have been added
            if (val.length !== 0) {
              prepareFilterObj(v, k)
              if (!_.has(filterValues.filters[v.collection][k], 'values')) filterValues.filters[v.collection][k].values = {}
              // Add values to filter object
              filterValues.filters[v.collection][k].values[key] = val
            }
          })
        }
      })
      if (keywords) filterValues.keywords = keywords // If keywords were defined, add them to the query
      if (sort) filterValues.sort = sort // If a sort value was defined, add it to the query

      return new Promise((resolve, reject) => {
        api.call('getResults', {
          clientId: state.user.client._id,
          params: {
            filterValues,
            page: page || 0,
            count: API_CONFIG.resultsCount
          }
        }, results => {
          let endOfResults = false
          if (results.length === 0 || results.length < API_CONFIG.resultsCount) endOfResults = true
          resolve({results, endOfResults})
        }, err => error(err, reject))
      })
    },
    getReport ({ commit, state }, id) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        api.call('getReport', {
          clientId: state.user.client._id,
          reportId: id
        }, report => {
          resolve(report)
        }, err => error(err, reject))
      })
    },
    postSync ({ commit }) {
      commit('setLoading', true)
      return new Promise((resolve, reject) => {
        api.call('postSync', null, () => {
          store.dispatch('setNotification', {
            message: i18n.t('notification.sync')
          })
          resolve()
        }, err => error(err, reject))
      })
    },
    postFiles (context, files) {
      return new Promise((resolve, reject) => {
        api.call('postFiles', {
          data: files
        }, () => {
          resolve()
        }, err => error(err, reject))
      })
    },
    getFile (context, url) {
      return new Promise((resolve, reject) => {
        api.getFile(url, null, blob => {
          const objectUrl = URL.createObjectURL(blob)
          resolve(objectUrl)
        }, err => reject(err))
      })
    },
    getFiles (context, urls) {
      return new Promise((resolve, reject) => {
        api.call('getFiles', {
          params: { urls }
        }, res => {
          const objectUrls = []
          _.each(res, item => {
            const blob = new Blob([new Uint8Array(item.data)])
            const objectUrl = URL.createObjectURL(blob)
            objectUrls.push(objectUrl)
          })
          resolve(objectUrls)
        }, err => reject(err))
      })
    }
  }
})

export default store
